import React, { lazy, memo, useCallback, useMemo } from "react";
import "../UpToDateComponents/UpToDateLinks/UpToDateLinks.css";
import { StaticVariables } from "../../../Utils/StaticVariables";
import YesterdayLinksLoader from "../UpToDateComponents/YesterdayLinks/YesterdayLinksLoader";
import NewsletterData from "../UpToDateComponents/NewsletterData/NewsletterData";
import NewsletterPopupData from "../UpToDateComponents/NewsletterData/NewsletterPopupData";
import FormData from "../UpToDateComponents/FormData/FormData";
import ReelsData from "../UpToDateComponents/ReelsData/ReelsData";
import TiktokData from "../UpToDateComponents/TikTokData/TiktokData";
import InstagramStoryData from "../UpToDateComponents/InstagramStoryData/InstagramStoryData";
import PodcastData from "../UpToDateComponents/PodcastData/PodcastData";
import YoutubeData from "../UpToDateComponents/YoutubeData/YoutubeData";
import FeaturedData from "../UpToDateComponents/FeaturedData/FeaturedData";
import BannerData from "../UpToDateComponents/BannerData/BannerData";
import HighlightDataSection from "../UpToDateComponents/HighlightDataSection/HighlightDataSecton";
import EmbedFormComponentFrontend from "../../../Screens/DashboardView/StayUpToDate/LandingPageComponents/EmbedFormComponent/EmbedFormComponentFrontend";
import LinksComponentFrontend from "../../../Screens/DashboardView/StayUpToDate/LandingPageComponents/LinksComponent/LinksComponentFrontend";
import CollectionComponentFrontend from "../../../Screens/DashboardView/StayUpToDate/StayUpToDateControlsComponents/CollectionComponent/CollectionComponentFrontend";
import TwitterData from "../UpToDateComponents/TwitterData/TwitterData";
import LinkedinData from "../UpToDateComponents/LinkedinData/LinkedinData";
import QueryLinkDataFront from "../UpToDateComponents/QueryLinkData/QueryLinkDataFront";
import YesterdayContentFront from "../UpToDateComponents/YesterdayContent/YesterdayContentFront";
import TrendingProductsDataFront from "../UpToDateComponents/TrendingProductsData/TrendingProductsDataFront";
import QueryContentDataFront from "../UpToDateComponents/QueryContentData/QueryContentDataFront";
import CustomLinkDataFront from "../UpToDateComponents/CustomLinkData/CustomLinkDataFront";
import CustomContentDataFront from "../UpToDateComponents/CustomContentData/CustomContentDataFront";
import SellerProductDashboard from "../../../Screens/DashboardView/StayUpToDate/StayUpToDateComponents/TemplateWrapper/SellerProductDashboard";
import MyStoreProducts from "../../../Screens/DashboardView/StayUpToDate/StayUpToDateControlsComponents/TemplateWrapper/MyStoreProducts";
import CampaignForm from "../../../Screens/DashboardView/EmailFlow/CamapignForm";
import { useDesign } from "../../../context/DesignContext";
import RenderIndividualUI from "../StayUpToDateIndividuelComponent/RenderIndividualUI";
import LatestPodcastData from "../UpToDateComponents/LatestPodcastData/LatestPodcastData";

const dayMappings: { [key: string]: number[] } = StaticVariables().dayMappings;

// Day-based display names for faster lookup
const DAY_BASED_DISPLAYS = new Set(["Yesterday", "This Week", "This Month"]);

function RenderComponentLoader(props: any) {
  const {
    componentId,
    user_id,
    username,
    brand_color,
    handleRedirect,
    isHandleNavigate,
    data,
    from,
    overrideDesignTokens,
    /** When true (e.g. website template shop/content tabs), post grids use RenderIndividualUI + same CSS as /:username/:id. */
    individualPostLayout,
    /** When `from` is website-template: labels loading state for shop vs content grids. */
    templatePageVariant,
    templateDevice,
    templateGridColumns,
    /** Website template: notify parent when feed resolves empty so the tab can be hidden. */
    onFeedResolved,
    isMobileView = false,
    creatorPageSlug,
    /** Website LiB editor: keep empty feeds visible with a "No data" placeholder. */
    showEmptyInEditor = false,
  } = props;
  // Website template preview is a framed width, not the browser window — treat
  // `templateDevice === "mobile"` the same as an explicit mobile landing view
  // so banners / highlights / card grids stack instead of staying desktop.
  const mobileView = Boolean(isMobileView) || templateDevice === "mobile";
  const {
    fontFamily,
    primaryFont,
    secondaryFont,
    primaryColor,
    secondaryColor,
    tertiaryColor,
    templateName,
    forthColor,
  } = useDesign();
  // When an overrideDesignTokens prop is provided (e.g. from the live design preview),
  // use those values so that swatch changes reflect immediately without requiring a Save.
  const designTokens = overrideDesignTokens
    ? {
        fontFamily: overrideDesignTokens.fontFamily ?? fontFamily,
        primaryColor: overrideDesignTokens.primaryColor ?? primaryColor,
        secondaryColor: overrideDesignTokens.secondaryColor ?? secondaryColor,
        tertiaryColor: overrideDesignTokens.tertiaryColor ?? tertiaryColor,
        forthColor: overrideDesignTokens?.forthColor ?? forthColor,
        templateName: overrideDesignTokens.templateName ?? templateName,
        primaryFontFamily: overrideDesignTokens.primaryFontFamily ?? primaryFont,
        secondaryFontFamily: overrideDesignTokens.secondaryFontFamily ?? secondaryFont,
      }
    : {
        fontFamily,
        primaryFontFamily: primaryFont,
        secondaryFontFamily: secondaryFont,
        primaryColor,
        secondaryColor,
        tertiaryColor,
        templateName,
        forthColor
      };
  // Destructure once and memoize
  const componentData = useMemo(
    () => ({
      displayName: String(data?.display_name ?? "").trim(),
      name: data?.name || "",
      type: data?.type || "",
      // Keep "" as empty selection — `|| []` hid clears because "" is falsy.
      postIds: data?.post_ids ?? "",
      details: data?.details,
      removedPostIds: data?.removed_postids,
      query: data?.query,
      cta: data?.cta_placeholder,
      backgroundColor: data?.background_color,
    }),
    [data],
  );

  const {
    displayName,
    name,
    type,
    postIds,
    details,
    removedPostIds,
    query,
    cta,
    backgroundColor,
  } = componentData;

  // Memoize numberOfDays calculation
  const numberOfDays = useMemo(() => {
    if (displayName === "Yesterday") return [1];
    if (displayName === "This Week" || displayName === "This Week Content")
      return [7];
    if (displayName === "This Month") return [30];
    if (dayMappings[displayName]) return [];
    return null;
  }, [displayName]);

  // Determine if it's a day-based component
  const isDayBased = useMemo(
    () =>
      DAY_BASED_DISPLAYS.has(displayName) || Boolean(dayMappings[displayName]),
    [displayName],
  );

  const templateGridLoaderProps = useMemo(
    () =>
      from === "website-template"
        ? {
            variant: templatePageVariant === "shop" ? "shop" : "content",
            device: templateDevice,
            columns: templateGridColumns,
          }
        : null,
    [from, templatePageVariant, templateDevice, templateGridColumns],
  );

  // Attach this row's id so Post Click analytics can bucket by component id
  // (duplicate titles are allowed).
  const trackRedirect = useCallback(
    (
      postId: string,
      media_url: string,
      platform?: any,
      name?: any,
      title?: any,
      isLinks?: any,
      fromSrc?: any,
    ) => {
      if (typeof handleRedirect !== "function") return;
      return handleRedirect(
        postId,
        media_url,
        platform,
        name,
        title,
        isLinks,
        fromSrc,
        data,
      );
    },
    [handleRedirect, data],
  );

  // Common props object to reduce repetition
  const commonProps = useMemo(
    () => ({
      user_id,
      username,
      brand_color,
      handleRedirect: trackRedirect,
      isHandleNavigate,
      component: data,
      from,
      designTokens,
      ...(individualPostLayout ? { RenderUI: RenderIndividualUI } : {}),
      onFeedResolved,
      templatePageVariant,
      templateDevice,
      templateGridColumns,
      templateGridLoaderProps,
      isMobileView: mobileView,
      creatorPageSlug,
      showEmptyInEditor,
    }),
    [
      user_id,
      username,
      brand_color,
      trackRedirect,
      isHandleNavigate,
      data,
      from,
      designTokens,
      individualPostLayout,
      onFeedResolved,
      templatePageVariant,
      templateDevice,
      templateGridColumns,
      templateGridLoaderProps,
      mobileView,
      creatorPageSlug,
      overrideDesignTokens,
      showEmptyInEditor,
    ],
  );

  // Memoized render content
  const content = useMemo(() => {
    // Latest Product Feed is stored as type `reels` + feedKind product-feed.
    // Strip preview uses featured product RPC — route destination the same way
    // instead of Instagram FEED-only ReelsData.
    const isLatestProductFeed = (() => {
      if (type !== "reels") return false;
      const filterLc = String(data?.shoppable_content_filter ?? "")
        .trim()
        .toLowerCase();
      if (filterLc === "products") return true;
      const raw = String(data?.details ?? "").trim();
      if (!raw.startsWith("{")) return false;
      try {
        return (JSON.parse(raw) as { feedKind?: string })?.feedKind ===
          "product-feed";
      } catch {
        return false;
      }
    })();

    if (isLatestProductFeed) {
      return (
        <FeaturedData
          {...commonProps}
          numberOfDays={7}
          title={name}
          removedPostIds={removedPostIds}
          isLinks={true}
          from={from}
        />
      );
    }

    // Match classic LiB: only "This Week Content" uses the content RPC.
    // Yesterday / This Week / This Month are shoppable-links carousels
    // (get_yesterday_links). shoppable_content_filter=content only picks
    // content platforms inside that loader — it does not change type.
    if (displayName === "This Week Content") {
      return (
        <YesterdayContentFront
          {...commonProps}
          numberOfDays={numberOfDays}
          title={name}
          isLinks={true}
          postIds={postIds}
          removedPostIds={removedPostIds}
          type={type}
        />
      );
    }

    // Handle day-based components (LiB / shop surfaces)
    if (isDayBased) {
      return (
        <YesterdayLinksLoader
          {...commonProps}
          numberOfDays={numberOfDays}
          title={name}
          isLinks={true}
          postIds={postIds}
          removedPostIds={removedPostIds}
          type={type}
          componentType={dayMappings[displayName] ? "daywise" : ""}
        />
      );
    }

    // Hand-picked carousels store the user title in display_name. Platform
    // keys below (linkedin, tiktok, …) must not win over type=content/links.
    if (type === "content") {
      return (
        <CustomContentDataFront
          {...commonProps}
          title={name}
          postIds={postIds}
          type={data?.type}
          isLinks={false}
        />
      );
    }
    if (type === "links" || type === "shoppable") {
      return (
        <CustomLinkDataFront
          {...commonProps}
          title={name}
          postIds={postIds}
          type={data?.type}
          isLinks={true}
        />
      );
    }

    // Handle special display names
    switch (displayName) {
      case "Inline Newsletter":
        return (
          <NewsletterData
            {...commonProps}
            numberOfDays={7}
            title={name}
            postIds={postIds}
            isLinks={true}
            component={data}
            handleRedirect={trackRedirect}
            brand_color={brand_color}
            isHandleNavigate={isHandleNavigate}
            from={from}
            flowdeskId={details?.split(",")[0]}
          />
        );

      case "Popup Newsletter":
        return (
          <NewsletterPopupData
            {...commonProps}
            numberOfDays={7}
            title={name}
            postIds={postIds}
            isLinks={true}
            flowdeskId={details?.split(",")[0]}
          />
        );

      case "My Store Product":
        return (
          <MyStoreProducts
            component={data}
            brand_color={brand_color}
            from={from === "preview" ? "preview" : "frontend"}
            designTokens={designTokens}
            isMobileView={mobileView}
          />
        );

      case "Form":
        return (
          <FormData
            {...commonProps}
            numberOfDays={7}
            title={name}
            postIds={postIds}
            isLinks={true}
            filloutId={details}
          />
        );

      case "Form":
        return (
          <FormData
            key={data.id}
            user_id={user_id}
            username={username}
            numberOfDays={7}
            title={name}
            postIds={postIds}
            isLinks={true}
            component={data}
            handleRedirect={trackRedirect}
            brand_color={brand_color}
            filloutId={data.details}
            isHandleNavigate={isHandleNavigate}
            from={from}
            designTokens={designTokens}
            removedPostIds={removedPostIds}
          />
        );

      case "reels":
        return (
          <ReelsData
            {...commonProps}
            numberOfDays={7}
            title={name}
            handleRedirect={trackRedirect}
            brand_color={brand_color}
            isHandleNavigate={isHandleNavigate}
            component={data}
            from={from}
            isLinks={false}
            removedPostIds={removedPostIds}
          />
        );

      case "tiktok":
        return (
          <TiktokData
            {...commonProps}
            numberOfDays={7}
            title={name}
            handleRedirect={trackRedirect}
            brand_color={brand_color}
            isHandleNavigate={isHandleNavigate}
            component={data}
            from={from}
            isLinks={false}
            removedPostIds={removedPostIds}
          />
        );

      case "instagram story":
        return (
          <InstagramStoryData
            {...commonProps}
            numberOfDays={15}
            title={name}
            handleRedirect={trackRedirect}
            brand_color={brand_color}
            isHandleNavigate={isHandleNavigate}
            component={data}
            from={from}
            isLinks={false}
            removedPostIds={removedPostIds}
          />
        );

      case "podcast":
        return (
          <PodcastData
            {...commonProps}
            numberOfDays={7}
            title={name}
            isLinks={false}
            from={from}
            removedPostIds={removedPostIds}
          />
        );

      case "latest-podcast":
        return(
          <LatestPodcastData
            {...commonProps}
          />
        );
      case "twitter":
        return (
          <TwitterData
            {...commonProps}
            numberOfDays={7}
            title={name}
            isLinks={false}
            from={from}
            removedPostIds={removedPostIds}
          />
        );

      case "linkedin":
        return (
          <LinkedinData
            {...commonProps}
            numberOfDays={7}
            title={name}
            isLinks={false}
            from={from}
            type="POST"
            removedPostIds={removedPostIds}
          />
        );

      case "youtube":
        return (
          <YoutubeData
            {...commonProps}
            numberOfDays={7}
            title={name}
            isLinks={false}
            from={from}
            type={type}
            removedPostIds={removedPostIds}
          />
        );

      case "featured":
        return (
          <FeaturedData
            {...commonProps}
            numberOfDays={7}
            title={name}
            removedPostIds={removedPostIds}
            isLinks={true}
            from={from}
          />
        );
      case "all":
        return (
          <FeaturedData
            {...commonProps}
            platform="all"
            type="ALL"
            numberOfDays={7}
            title={name}
            removedPostIds={removedPostIds}
            isLinks={true}
            from={from}
          />
        );
      case "videos":
      default:
        if (type === "content") {
          return (
            <CustomContentDataFront
              {...commonProps}
              title={name}
              postIds={postIds}
              type={data?.type}
              isLinks={false}
            />
          );
        } else if (type === "products-carousel") {
          return (
            <SellerProductDashboard
              key={data.id}
              title={name}
              isLinks={true}
              brand_color={brand_color}
              cta={data?.cta_placeholder}
              type={type}
              from={from}
              user_id={user_id}
              username={username}
            />
          );
        } else if (type === "links-section") {
          return (
            <LinksComponentFrontend
              key={data.id}
              user_id={user_id}
              username={username}
              numberOfDays={7}
              title={name}
              component={data}
              brand_color={brand_color}
              // isLinks={true}
              handleRedirect={trackRedirect}
              from={from}
              pageFrom={from}
              isMobileView={mobileView}
              designTokens={designTokens}
            />
          );
        } else if (type === "links" || type === "shoppable") {
          return (
            <CustomLinkDataFront
              {...commonProps}
              title={name}
              postIds={postIds}
              type={data?.type}
              isLinks={true}
            />
          );
        } else if (type === "query-content" || type === "content-series") {
          return (
            <QueryContentDataFront
              {...commonProps}
              title={name}
              postIds={postIds}
              query={data?.query}
              removedPostIds={data?.removed_postids}
              type={type === "content-series" ? "query-content" : data?.type}
              isLinks={false}
            />
          );
        } else if (type === "query-links") {
          return (
            <QueryLinkDataFront
              {...commonProps}
              title={name}
              postIds={postIds}
              query={data?.query}
              removedPostIds={data?.removed_postids}
              type={data?.type}
              isLinks={true}
            />
          );
        } else if (type === "banner") {
          return (
            <BannerData
              key={data.id}
              user_id={user_id}
              username={username}
              numberOfDays={7}
              title={name}
              postIds={postIds}
              isLinks={true}
              component={data}
              handleRedirect={trackRedirect}
              brand_color={brand_color}
              isHandleNavigate={isHandleNavigate}
              from={from}
              designTokens={designTokens}
              isMobileView={mobileView}
            />
          );
        } else if (type === "highlight") {
          return (
            <HighlightDataSection
              key={data.id}
              user_id={user_id}
              username={username}
              numberOfDays={7}
              title={name}
              postIds={postIds}
              data={data}
              handleRedirect={trackRedirect}
              // handleShop={handleShop}
              brand_color={brand_color}
              component={data}
              isHandleNavigate={isHandleNavigate}
              from={from}
              designTokens={designTokens}
              isMobileView={mobileView}
            />
          );
        } else if (
          type === "trending products" ||
          type === "trending content" ||
          type === "trending-products" ||
          type === "trending-content"
        ) {
          return (
            <TrendingProductsDataFront
              key={data.id}
              user_id={user_id}
              username={username}
              title={name}
              postIds={postIds}
              handleRedirect={trackRedirect}
              brand_color={brand_color}
              details={data?.details}
              removedPostIds={data?.removed_postids}
              isHandleNavigate={isHandleNavigate}
              component={data}
              type={
                type === "trending-content"
                  ? "trending content"
                  : type === "trending-products"
                    ? "trending products"
                    : data?.type
              }
              isLinks={true}
              from={from}
              designTokens={designTokens}
              showEmptyInEditor={showEmptyInEditor}
              {...(individualPostLayout ? { RenderUI: RenderIndividualUI } : {})}
            />
          );
        } else if (type === "embed-form" || type === "newsletter-form") {
          return (
            <EmbedFormComponentFrontend
              key={data.id}
              user_id={user_id}
              username={username}
              numberOfDays={7}
              title={name}
              postIds={postIds}
              isLinks={true}
              component={data}
              handleRedirect={trackRedirect}
              brand_color={brand_color}
              flowdeskId={String(data?.details || "").split(",")[0]}
              isHandleNavigate={isHandleNavigate}
              from={from}
              designTokens={designTokens}
              isMobileView={mobileView}
            />
          );
        } else if (type === "campaign") {
          return (
            <CampaignForm
              key={data.id}
              user_id={user_id}
              username={username}
              title={name}
              postIds={postIds}
              isLinks={true}
              data={data}
              background_color={data.background_color}
              handleRedirect={trackRedirect}
              brand_color={brand_color}
              isHandleNavigate={isHandleNavigate}
              from={from}
              designTokens={designTokens}
              isMobileView={mobileView}
            />
          );
        }
    }

    // Handle type-based rendering
    // Platform Latest-tab types are also listed here so a mismatched/custom
    // display_name still mounts the loader (and thus handleFirstImage).
    switch (type) {
      case "reels":
        return (
          <ReelsData
            {...commonProps}
            numberOfDays={7}
            title={name}
            handleRedirect={trackRedirect}
            brand_color={brand_color}
            isHandleNavigate={isHandleNavigate}
            component={data}
            from={from}
            isLinks={false}
            removedPostIds={removedPostIds}
          />
        );

      case "tiktok":
        return (
          <TiktokData
            {...commonProps}
            numberOfDays={7}
            title={name}
            handleRedirect={trackRedirect}
            brand_color={brand_color}
            isHandleNavigate={isHandleNavigate}
            component={data}
            from={from}
            isLinks={false}
            removedPostIds={removedPostIds}
          />
        );

      case "instagram story":
        return (
          <InstagramStoryData
            {...commonProps}
            numberOfDays={15}
            title={name}
            handleRedirect={trackRedirect}
            brand_color={brand_color}
            isHandleNavigate={isHandleNavigate}
            component={data}
            from={from}
            isLinks={false}
            removedPostIds={removedPostIds}
          />
        );

      case "podcast":
        return (
          <PodcastData
            {...commonProps}
            numberOfDays={7}
            title={name}
            isLinks={false}
            from={from}
            removedPostIds={removedPostIds}
          />
        );

      case "twitter":
        return (
          <TwitterData
            {...commonProps}
            numberOfDays={7}
            title={name}
            isLinks={false}
            from={from}
            removedPostIds={removedPostIds}
          />
        );

      case "linkedin":
        return (
          <LinkedinData
            {...commonProps}
            numberOfDays={7}
            title={name}
            isLinks={false}
            from={from}
            type="POST"
            removedPostIds={removedPostIds}
          />
        );

      case "youtube":
        return (
          <YoutubeData
            {...commonProps}
            numberOfDays={7}
            title={name}
            isLinks={false}
            from={from}
            removedPostIds={removedPostIds}
          />
        );

      case "featured":
        return (
          <FeaturedData
            {...commonProps}
            numberOfDays={7}
            title={name}
            removedPostIds={removedPostIds}
            isLinks={true}
            from={from}
          />
        );

      case "all":
        return (
          <FeaturedData
            {...commonProps}
            platform="all"
            type="ALL"
            numberOfDays={7}
            title={name}
            removedPostIds={removedPostIds}
            isLinks={true}
            from={from}
          />
        );

      case "content":
        return (
          <CustomContentDataFront
            {...commonProps}
            title={name}
            postIds={postIds}
            type={type}
            isLinks={false}
          />
        );

      case "products-carousel":
        return (
          <SellerProductDashboard
            key={componentId}
            title={name}
            isLinks={true}
            brand_color={brand_color}
            cta={cta}
            type={type}
            from={from}
            user_id={user_id}
            username={username}
          />
        );

      case "links-section":
        return (
          <LinksComponentFrontend
            {...commonProps}
            numberOfDays={7}
            title={name}
            from="landing-page"
            pageFrom={from}
          />
        );

      case "links":
      case "shoppable":
        return (
          <CustomLinkDataFront
            {...commonProps}
            title={name}
            postIds={postIds}
            type={type}
            isLinks={true}
          />
        );

        case "carousel-collection":
          return (
            <CollectionComponentFrontend
              {...commonProps}
              title={name}
              postIds={postIds}
              type={type}
              isLinks={true}
              templatePageVariant={templatePageVariant}
            />
          );

      case "query-content":
      case "content-series":
        return (
          <QueryContentDataFront
            {...commonProps}
            title={name}
            postIds={postIds}
            query={query}
            removedPostIds={removedPostIds}
            type={type === "content-series" ? "query-content" : type}
            isLinks={false}
          />
        );

      case "query-links":
        return (
          <QueryLinkDataFront
            {...commonProps}
            title={name}
            postIds={postIds}
            query={query}
            removedPostIds={removedPostIds}
            type={type}
            isLinks={true}
          />
        );

      case "banner":
        return (
          <BannerData
            {...commonProps}
            numberOfDays={7}
            title={name}
            postIds={postIds}
            isLinks={true}
          />
        );

      case "highlight":
        return (
          <HighlightDataSection
            {...commonProps}
            numberOfDays={7}
            title={name}
            postIds={postIds}
            data={data}
          />
        );

      case "trending products":
      case "trending content":
      case "trending-products":
      case "trending-content":
        return (
          <TrendingProductsDataFront
            key={data?.id}
            {...commonProps}
            title={name}
            postIds={postIds}
            details={details}
            removedPostIds={removedPostIds}
            type={type === "trending-content" ? "trending content" : type === "trending-products" ? "trending products" : type}
            isLinks={true}
          />
        );

      case "embed-form":
      case "newsletter-form":
        return (
          <EmbedFormComponentFrontend
            {...commonProps}
            numberOfDays={7}
            title={name}
            postIds={postIds}
            isLinks={true}
            flowdeskId={details?.split(",")[0]}
          />
        );

      case "campaign":
        return (
          <CampaignForm
            {...commonProps}
            title={name}
            postIds={postIds}
            isLinks={true}
            data={data}
            background_color={backgroundColor}
          />
        );

      default:
        return null;
    }
  }, [
    displayName,
    isDayBased,
    type,
    commonProps,
    numberOfDays,
    name,
    postIds,
    removedPostIds,
    details,
    query,
    cta,
    backgroundColor,
    data,
    componentId,
    brand_color,
    from,
    user_id,
    username,
    individualPostLayout,
    templatePageVariant,
    onFeedResolved,
  ]);

  return content;
}

function overrideDesignTokensShallowEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    a.primaryColor === b.primaryColor &&
    a.secondaryColor === b.secondaryColor &&
    a.tertiaryColor === b.tertiaryColor &&
    a.templateName === b.templateName &&
    a.fontFamily === b.fontFamily &&
    a.primaryFontFamily === b.primaryFontFamily &&
    a.secondaryFontFamily === b.secondaryFontFamily
  );
}

// Memo: design preview must always re-render (carousel data, tokens, layout); memo caused stale/empty UI.
export default memo(RenderComponentLoader, (prevProps, nextProps) => {
  if (
    prevProps.from === "preview" ||
    nextProps.from === "preview" ||
    prevProps.from === "website-template" ||
    nextProps.from === "website-template"
  ) {
    return false;
  }
  return (
    prevProps.componentId === nextProps.componentId &&
    prevProps.user_id === nextProps.user_id &&
    prevProps.username === nextProps.username &&
    prevProps.brand_color === nextProps.brand_color &&
    prevProps.data === nextProps.data &&
    String(prevProps.data?.post_ids ?? "") ===
      String(nextProps.data?.post_ids ?? "") &&
    String(prevProps.data?.custom_order ?? "") ===
      String(nextProps.data?.custom_order ?? "") &&
    String(prevProps.data?.content_order ?? "") ===
      String(nextProps.data?.content_order ?? "") &&
    // Trending min-clicks / date window live in `details` — without this,
    // LiB edit pills updated localData but the memo kept the old feed painted.
    String(prevProps.data?.details ?? "") ===
      String(nextProps.data?.details ?? "") &&
    String(prevProps.data?.query ?? "") ===
      String(nextProps.data?.query ?? "") &&
    prevProps.from === nextProps.from &&
    prevProps.isHandleNavigate === nextProps.isHandleNavigate &&
    overrideDesignTokensShallowEqual(
      prevProps.overrideDesignTokens,
      nextProps.overrideDesignTokens,
    ) &&
    prevProps.individualPostLayout === nextProps.individualPostLayout &&
    prevProps.templatePageVariant === nextProps.templatePageVariant &&
    prevProps.onFeedResolved === nextProps.onFeedResolved
  );
});
